import React, { Fragment } from "react";
import { Combobox, Transition } from "@headlessui/react";
import { IonIcon } from "@ionic/react";
import { chevronDown } from "ionicons/icons";
import { City } from "./types";
import CommonFormElement from "../UI/CommonFormElements/CommonFormElement";
import classNames from "classnames/bind";
import styles from "./AddressForm.module.scss";
import commonFormStyles from "../UI/CommonFormElements/CommonFormElements.module.scss";
import { useTranslation } from "react-i18next";

const cx = classNames.bind(styles);
const commonForm = classNames.bind(commonFormStyles);

interface CityComboboxProps {
  value: string;
  invalid: boolean;
  disabled: boolean;
  filteredCities: City[];
  onChange: (value: string) => void;

  citySelectContainer?: string;
  dropdownList?: string;
}

export const CityCombobox = ({
  value,
  onChange,
  filteredCities,
  invalid,
  disabled,
  citySelectContainer,
  dropdownList,
}: CityComboboxProps) => {
  const { t } = useTranslation();
  const handlePreventTabKey = (e: React.KeyboardEvent) => {
    if (e.key === "Tab") {
      e.preventDefault();
    }
  };

  return (
    <Combobox value={value} disabled={disabled} onChange={onChange}>
      {({ open }) => (
        <div className={cx("relative w-full", citySelectContainer)}>
          <CommonFormElement
            isRequired
            isValid={!invalid}
            label={t("Locality")}
            errorMessage={invalid ? t("Select locality") : ""}
          >
            <Combobox.Button className="w-full relative">
              <Combobox.Input
                value={value}
                onKeyDown={handlePreventTabKey}
                displayValue={(cityName: string) => cityName}
                onChange={(event) => onChange(event.target.value)}
                className={commonForm(
                  "w-full rounded-full border-1 border-solid py-3 px-4 border-[#ddd]",
                  "focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-white/25",
                  { commonInvalid: invalid },
                )}
              />
              <IonIcon icon={chevronDown} className="absolute bottom-4 right-4 size-4" />
            </Combobox.Button>
          </CommonFormElement>

          <Transition
            as={Fragment}
            show={open}
            key="transition"
            leaveTo="opacity-0"
            leaveFrom="opacity-100"
            leave="transition ease-in duration-100"
          >
            <Combobox.Options
              className={cx(
                "absolute inset-xy-0 top-24 bg-white z-50",
                "w-full rounded-xl border-1 p-1",
                "transition duration-100 ease-in",
                dropdownList,
              )}
            >
              {filteredCities?.length > 0 ? (
                filteredCities.map((city) => (
                  <Combobox.Option
                    key={city.id}
                    value={city.name}
                    className="flex items-center gap-2 rounded-lg py-1.5 px-3 active:bg-[#ddd]"
                  >
                    {city.name}
                  </Combobox.Option>
                ))
              ) : (
                <div className="py-1.5 px-3 text-gray-500">{t("Nothing found")}</div>
              )}
            </Combobox.Options>
          </Transition>
        </div>
      )}
    </Combobox>
  );
};
